// ante-react-kit/src/components/v2/hooks/useVoucherData.js
import { useState, useRef, useCallback, useEffect, useMemo, useContext } from 'react';
import { markVoucherViewed, shouldShowBootSkeleton, bootKeyOf } from '../../AntVoucherSkeleton/bootTracker';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import stringify from '../../../utils/stableStringify';
import AntCondition from '../../../utils/AntCondition';
import AntConfig from '../../../utils/AntConfig';
import AntLocalization from '../../../utils/AntLocalization';
import AntAuth from '../../../utils/AntAuth';
import { applyPreparedSnapshot } from '../../../utils/AntSnapshotTransport';
import cs from '../../../utils/const';
import { AntGlobal, AntSession, AntSidebarContext } from '../../../utils/AntContext';

// Fields không tính là "thay đổi" khi compare old vs new
var EXCLUDE_FIELDS = { actions: 1, edit: 1, infos: 1, checksum: 1, recalc_checksum: 1, updated_at: 1 };

export default function useVoucherData(props) {
    var params = useParams();
    var location = useLocation();
    var navigate = useNavigate();
    var locationPath = String(location.pathname);
    var appGlobal = AntGlobal();
    var session = AntSession();

    // --- Init API entityDisplay ---
    if (props.API && !props.API.entityDisplay) {
        if (appGlobal && appGlobal.appConfig && appGlobal.appConfig.Services) {
            var found = appGlobal.appConfig.Services.find(function (s) { return s.name === props.API.entityName; });
            if (found) props.API.init({ entityDisplay: found.display });
        }
    }

    // --- Config ---
    var mainTable = props.table ? props.table : props.API ? props.API.entityName : '';
    var mainEntityName = props.API && props.API.entityName ? props.API.entityName : '';
    var mainEntityDisplay = props.API && props.API.entityDisplay ? props.API.entityDisplay : props.entityDisplay ? props.entityDisplay : '';

    var mainConfig = useMemo(function () {
        if (!mainTable) return new AntConfig(appGlobal, mainTable);

        var foundArr = appGlobal.tableConfigs;
        if (foundArr instanceof Array) {
            var existing = foundArr.find(function (t) { return t.tableName === mainTable; });
            if (existing) return existing.config;
        }

        var cnf = new AntConfig(appGlobal, mainTable);
        if (!(appGlobal.tableConfigs instanceof Array)) appGlobal.tableConfigs = [];
        appGlobal.tableConfigs.push({ tableName: mainTable, config: cnf });
        return cnf;
    }, []);

    // --- State ---
    var [mainData, setMainDataRaw] = useState({});
    var mainDataRef = useRef(mainData);
    mainDataRef.current = mainData;
    var [flowData, setFlowData] = useState(null);
    var [flowVisible, setFlowVisible] = useState(false);
    // Khung xám CHỈ cho lần đầu mở MỘT phiếu (mã/số định danh đổi). Mọi lượt tải lại
    // phiếu đang xem KHÔNG dựng lại khung xám — giữ nguyên số cũ trên màn hình.
    var [bootCode, setBootCode] = useState(function () {
        var key = bootKeyOf(params);
        return shouldShowBootSkeleton(key) ? key : null;
    });
    var [mainState, setMainState] = useState({
        error: null,
        oldData: null,
        loading: true,
        toolbarProcessingButton: null,
        tabStyle: null
    });

    // --- Refs ---
    var mainCodeRef = useRef(null);
    var mainIDRef = useRef(null);
    var isPreparedRef = useRef(false);
    var isSavingRef = useRef(false);
    var mountedRef = useRef(true);
    var lockRef = useRef({ saving: false, loading: false });
    var quickReloadTimerRef = useRef(null);

    // --- Sidebar Context ---
    var _sidebarCtx = useContext(AntSidebarContext);
    var sidebarContext = useMemo(function () {
        if (!mainData || !mainData.id) return null;
        return {
            voucherType: mainEntityName,
            voucherTypeLabel: mainEntityDisplay,
            voucherModule: props.API ? props.API.moduleName : null,
            voucherCode: mainData.code,
            voucherId: mainData.id,
            data: mainData
        };
    }, [mainData ? mainData.id : null, mainData ? mainData.code : null, mainEntityName]);

    useEffect(function syncSidebarContext() {
        if (_sidebarCtx && _sidebarCtx.setContext) {
            _sidebarCtx.setContext(sidebarContext);
        }
        return function () {
            if (_sidebarCtx && _sidebarCtx.setContext) _sidebarCtx.setContext(null);
        };
    }, [sidebarContext ? sidebarContext.voucherId : null, sidebarContext ? sidebarContext.voucherCode : null]);

    // --- Computed values ---
    var mainInfos = useMemo(function () {
        var ar = [];
        if (mainData && mainData.infos instanceof Array && mainData.infos.length > 0) {
            if (props.onProcessInfo) ar = props.onProcessInfo(mainData.infos);
            else ar = mainData.infos;
        }
        return ar;
    }, [mainData]);

    var isRenderPage = useMemo(function () {
        return AntCondition.isRender(mainData, props);
    }, [mainData]);

    var isVisiblePage = Boolean(mainData && mainIDRef.current);

    var isVisibleHeader = useMemo(function () {
        return props.header && props.header.items && props.header.items.length > 0 && AntCondition.isVisible(mainData, props.header);
    }, [mainData]);

    var isVisibleFooter = useMemo(function () {
        return props.footer && props.footer.items && props.footer.items.length > 0 && AntCondition.isVisible(mainData, props.footer);
    }, [mainData]);

    var isInValidData = useMemo(function () {
        return (mainIDRef.current || mainCodeRef.current) && mainData && !mainIDRef.current;
    }, [mainState, mainData]);

    var sessionValid = useMemo(function () {
        var _canRender = true;
        if (props.table === 'ACUserAccount') {
            _canRender = AntCondition.isValid(null, {
                whenSession: { session_status: { type: '!=', value: 'Restricted' }, user_group_code: 'Admin' },
                whenSessionType: 'OR'
            });
        } else {
            _canRender = AntCondition.isValid(null, {
                whenSession: { session_status: { type: '!=', value: 'Restricted' } }
            });
        }
        return _canRender;
    }, []);

    var isPermDeniedData = useMemo(function () {
        return sessionValid && (mainIDRef.current || mainCodeRef.current) && mainData && mainData.errorCode === cs.erPermissionDenied.code;
    }, [mainState, mainData, sessionValid]);

    var renderLoading = isRenderPage && !isPermDeniedData && (mainState.loading || !isPreparedRef.current);
    var renderAccessDenied = !isRenderPage || isPermDeniedData;
    var renderNotExistData = isRenderPage && !isPermDeniedData && !renderLoading && isPreparedRef.current && isInValidData;

    // --- setError ---
    var setError = useCallback(function setError(content, errors) {
        if (!content && (mainState.error === null || mainState.error === undefined)) return;
        if (errors === 'TypeError: Failed to fetch') {
            errors = { vi: 'Không thể kết nối đến hệ thống !', en: 'Can not connect to system !', fr: 'Impossible de se connecter au système !', es: '¡No se puede conectar al sistema!', id: 'Tidak dapat terhubung ke sistem!', ja: 'システムに接続できません！', zh: '无法连接到系统！', ko: '시스템에 연결할 수 없습니다!', hi: 'सिस्टम से कनेक्ट नहीं हो सकता!', ar: 'لا يمكن الاتصال بالنظام!' };
        }
        var values = errors === undefined || errors === null ? [] : errors instanceof Array ? errors : [errors];
        content = values.length === 1 ? values[0] : content;

        setMainState(function (v) {
            return Object.assign(v, {
                error: content ? { text: String(content), values: values } : null
            });
        });

        if (props.onError) props.onError(content, errors);
    }, [mainState, props.onError]);

    // --- loadContent ---
    var loadContent = useCallback(async function loadContent(opts) {
        opts = opts || {};
        if (!props.API || !props.API.postUpdatePrepare) return;

        var id = params && params.id ? params.id : undefined;
        var code = params && params.code ? params.code : undefined;
        if (!code && !id) return;

        if (opts.changedFields && props.refreshOnChangedFields &&
            !opts.changedFields.some(function (f) { return props.refreshOnChangedFields.includes(f); })) {
            return;
        }

        var obj = opts.newObj || null;

        if (!opts.isRefresh) isPreparedRef.current = false;
        lockRef.current.loading = true;

        var prepareOpts = Object.assign({}, props.prepareConditions, props.header ? props.header.prepareConditions : undefined);
        if (opts.forcePrepare) prepareOpts.forcePrepare = true;
        if (props.isSetting) prepareOpts.isSetting = true;

        try {
            obj = await props.API.postUpdatePrepare(
                id,
                code,
                Boolean(opts && typeof obj === 'object' && opts.recalcable),
                prepareOpts,
                opts.bypassSnapshot ? undefined : mainDataRef.current
            );

            if (!mountedRef.current) return;

            if (obj) {
                if (!obj.edit) obj.edit = {};

                var ar1 = [];
                if (obj.edit.always_editable_fields && obj.edit.always_editable_fields instanceof Array) ar1 = ar1.concat(obj.edit.always_editable_fields);
                if (ar1.indexOf('code') < 0) ar1.push('code');

                var ar2 = [];
                if (obj.edit.none_validate_fields && obj.edit.none_validate_fields instanceof Array) ar2 = ar2.concat(obj.edit.none_validate_fields);
                if (ar2.indexOf('code') < 0) ar2.push('code');

                obj.edit = Object.assign({}, obj.edit, {
                    always_editable_fields: ar1,
                    none_validate_fields: ar2
                });

                mainDataRef.current = obj;
                setMainDataRaw(function (v) {
                    var newData = JSON.parse(stringify(v));
                    Object.keys(newData).forEach(function (key) { delete newData[key]; });
                    return Object.assign(newData, obj);
                });

                isPreparedRef.current = true;
                mainCodeRef.current = obj.code;
                mainIDRef.current = obj.id;
                markVoucherViewed(obj.code || obj.id);

                // Extract voucherFlow
                if (props.flow && obj.voucherFlow) {
                    var vf = obj.voucherFlow;
                    var hasData = vf && vf.nodes && vf.nodes.length > 1;
                    setFlowData(vf);
                    setFlowVisible(hasData && props.flow.display === 'always');
                    delete obj.voucherFlow;
                }

                setMainState(function (v) {
                    return Object.assign(v, {
                        error: null,
                        loading: false,
                        oldData: JSON.parse(stringify(obj))
                    });
                });
            }
        } catch (e) {
            if (mountedRef.current) {
                setError('Network error', e);
                setMainState(function (v) { return Object.assign(v, { loading: false }); });
            }
        } finally {
            lockRef.current.loading = false;
            if (mountedRef.current) setBootCode(null);
        }
        return obj;
    }, [params, props.API, props.refreshOnChangedFields, props.prepareConditions, props.isSetting, props.flow, setError]);

    // --- getValidData ---
    var getValidData = useCallback(async function getValidData(byPassValidate) {
        var obj = {};
        Object.keys(mainData).forEach(function (fieldName) {
            if (EXCLUDE_FIELDS[fieldName]) return;
            var objCurrent = mainData[fieldName];
            var objOld = mainState.oldData ? mainState.oldData[fieldName] : null;
            if (objCurrent !== objOld && stringify(objCurrent) !== stringify(objOld) &&
                (!['', null].includes(objCurrent) || !['', null].includes(objOld))) {
                obj[fieldName] = objCurrent;
            }
        });
        return Object.keys(obj).length > 0 ? obj : null;
    }, [mainData, mainState.oldData]);

    // --- saveOnFieldDataChanged ---
    var saveOnFieldDataChanged = useCallback(async function saveOnFieldDataChanged() {
        setError(null);

        var newObj = await getValidData(true);
        if (!newObj || Object.keys(newObj).length <= 0) return;

        if (props.onSaving) newObj = props.onSaving(mainData, newObj);

        isSavingRef.current = true;
        lockRef.current.saving = true;
        var error = null;

        var opts = { getNewPrepare: true };
        var whereObj = { id: mainIDRef.current, checksum: mainData.checksum };

        if (props.isSetting) {
            opts.isSetting = true;
            whereObj.isSetting = true;
        }

        try {
            var result = await props.API.postUpdate(whereObj, newObj, opts, mainDataRef.current);
            if (!result || result.error || !result.data || !result.data.updated) {
                error = mainConfig.translateFirstError(result);
            } else {
                await loadContent({
                    changedFields: Object.keys(newObj),
                    newObj: result.data
                });
            }
        } catch (e) {
            error = e;
        }

        isSavingRef.current = false;
        lockRef.current.saving = false;

        if (error) {
            var strEnd = AntLocalization.translateText({ vi: 'Không thể cập nhật thông tin !', en: 'Can not update data !', fr: 'Impossible de mettre à jour !', es: '¡No se puede actualizar!', id: 'Tidak dapat memperbarui!', ja: '更新できません！', zh: '无法更新！', ko: '업데이트 불가!', hi: 'अपडेट नहीं कर सकते!', ar: 'لا يمكن التحديث!' });
            setError(strEnd, error);
            if (props.onError) props.onError(strEnd, error);
            return false;
        } else if (props.onSaved) {
            props.onSaved(result.data);
        }
    }, [setError, getValidData, mainData, mainConfig, loadContent, props.onSaving, props.onSaved, props.isSetting, props.API, props.onError]);

    // --- navigateToReload ---
    var navigateToReload = useCallback(function navigateToReload(id, code) {
        var link = String(locationPath);
        var ar = link.split('/');
        if (ar.length > 0) ar.pop();
        link = ar.join('/');
        if (!link.startsWith('/')) link = '/' + link;
        if (!link.endsWith('/')) link = link + '/';
        link = link + (code ? code : id);
        navigate(link);
    }, [locationPath, navigate]);

    // --- onFieldDataChanged (from AntBaseForm) ---
    var onFieldDataChanged = useCallback(async function onFieldDataChanged(e) {
        if (e.code) {
            navigateToReload(null, e.code);
            return;
        }
        if (e.id) {
            navigateToReload(e.id, null);
            return;
        }
        if (props.onFieldDataChanged) {
            var isValid = props.onFieldDataChanged(e);
            if (!isValid) return;
        }
        await saveOnFieldDataChanged();
    }, [navigateToReload, props.onFieldDataChanged, saveOnFieldDataChanged]);

    // --- navigateHome ---
    var navigateHome = useCallback(function navigateHome() {
        var link;
        if (props.homeUrl) {
            link = props.homeUrl;
        } else {
            link = String(locationPath);
            var ar = link.split('/');
            if (ar.length > 0) ar.pop();
            link = ar.join('/');
            if (!link.startsWith('/')) link = '/' + link;
            link = link + 's';
        }
        navigate(link);
    }, [props.homeUrl, locationPath, navigate]);

    // --- reload ---
    var reload = useCallback(async function reload(opts) {
        if ((opts && opts.refreshMode === 'allPage') || props.refreshMode === 'allPage') {
            navigate('/reload', { replace: true });
            setTimeout(function () {
                navigate(locationPath, { replace: true });
            });
        } else {
            await loadContent(opts);
        }
    }, [props.refreshMode, loadContent, locationPath, navigate]);

    // --- load ---
    var load = useCallback(async function load() {
        var id = params && params.id ? params.id : mainIDRef.current;
        var code = params && params.code ? params.code : mainCodeRef.current;
        if (!id && !code) return;
        await loadContent({ recalcable: true });
    }, [params, loadContent]);

    // --- Socket listener ---
    useEffect(function registerSocket() {
        if (!session || !session.registerSocketListener) return;
        var reloadTimer = null;
        // Gộp tin dồn dập trong 400ms thành 1 lần tải lại (trước đây MỖI tin = 1 lần loadContent
        // → bão request khi đơn được tính lại liên tục), đồng bộ hành vi với AntVoucher bản 1.
        function scheduleReload() {
            if (reloadTimer) clearTimeout(reloadTimer);
            reloadTimer = setTimeout(function () {
                reloadTimer = null;
                loadContent(props.flow
                    ? { isRefresh: true, forcePrepare: true, bypassSnapshot: true }
                    : { isRefresh: true });
            }, 400);
        }
        function onSocketReceived(data) {
            if (data && data.byPassOfficeMode) return false;
            // Nối lại mạng: tin trong lúc đứt đã mất → tải lại phiếu đang mở
            if (data && data.event === 'SOCKET_RECONNECTED') {
                if (quickReloadTimerRef.current) {
                    clearTimeout(quickReloadTimerRef.current);
                    quickReloadTimerRef.current = null;
                }
                scheduleReload();
                return;
            }
            var payload = data && data.data;
            var isCurrentVoucher =
                mainTable === (data && data.name) &&
                ((payload && payload.code && (params.code === payload.code || mainCodeRef.current === payload.code)) ||
                    (payload && payload.id && String(mainIDRef.current) === String(payload.id)));
            if (isCurrentVoucher) {
                var prepared = applyPreparedSnapshot(mainDataRef.current, payload);
                if (prepared) {
                    if (props.flow && Object.prototype.hasOwnProperty.call(prepared, 'voucherFlow')) {
                        var nextFlow = prepared.voucherFlow || null;
                        setFlowData(nextFlow);
                        setFlowVisible(Boolean(nextFlow && nextFlow.nodes && nextFlow.nodes.length > 1 && props.flow.display === 'always'));
                        delete prepared.voucherFlow;
                    } else if (props.flow) {
                        scheduleReload();
                    }
                    var edit = prepared.edit && typeof prepared.edit === 'object' ? prepared.edit : {};
                    prepared.edit = Object.assign({}, edit, {
                        always_editable_fields: Array.from(new Set((edit.always_editable_fields || []).concat(['code']))),
                        none_validate_fields: Array.from(new Set((edit.none_validate_fields || []).concat(['code'])))
                    });
                    mainDataRef.current = prepared;
                    mainCodeRef.current = prepared.code;
                    mainIDRef.current = prepared.id;
                    isPreparedRef.current = true;
                    setMainDataRaw(prepared);
                    setMainState(function (state) {
                        return Object.assign({}, state, {
                            error: null,
                            loading: false,
                            oldData: JSON.parse(JSON.stringify(prepared))
                        });
                    });
                    if (quickReloadTimerRef.current) {
                        clearTimeout(quickReloadTimerRef.current);
                        quickReloadTimerRef.current = null;
                    }
                    return;
                }
                if (quickReloadTimerRef.current) {
                    clearTimeout(quickReloadTimerRef.current);
                    quickReloadTimerRef.current = null;
                }
                scheduleReload();
            }
        }
        session.clearSocketListener(mainTable);
        session.registerSocketListener(onSocketReceived, mainTable);
        return function () {
            session.clearSocketListener(mainTable);
            if (reloadTimer) clearTimeout(reloadTimer);
            if (quickReloadTimerRef.current) {
                clearTimeout(quickReloadTimerRef.current);
                quickReloadTimerRef.current = null;
            }
        };
    }, [locationPath, loadContent, params, props.flow]);

    // --- Mount/Unmount ---
    useEffect(function onMount() {
        mainCodeRef.current = null;
        AntAuth.checkValidSession();
        mountedRef.current = true;
        return function onUnmount() {
            markVoucherViewed(mainCodeRef.current || mainIDRef.current);
            mountedRef.current = false;
            mainCodeRef.current = null;
            mainIDRef.current = null;
        };
    }, []);

    // Chốt an toàn: máy chủ không trả sau 20 giây thì bỏ khung xám, tránh treo mãi.
    useEffect(function bootSkeletonGuard() {
        if (!bootCode) return;
        var timer = setTimeout(function () { setBootCode(null); }, 20000);
        return function () { clearTimeout(timer); };
    }, [bootCode]);

    // --- Load on params change ---
    useEffect(function onParamsChange() {
        if (params.code && params.code !== mainCodeRef.current) {
            mainCodeRef.current = params.code;
            setBootCode(shouldShowBootSkeleton(params.code) ? String(params.code) : null);
            load();
        } else if (params.id && params.id !== mainIDRef.current) {
            mainIDRef.current = params.id;
            setBootCode(shouldShowBootSkeleton(params.id) ? String(params.id) : null);
            load();
        }
    }, [params]);

    return {
        // State
        mainData: mainData,
        mainState: mainState,
        flowData: flowData,
        flowVisible: flowVisible,
        mainInfos: mainInfos,

        // State setters
        setMainData: setMainDataRaw,
        setMainState: setMainState,
        setFlowData: setFlowData,
        setFlowVisible: setFlowVisible,

        // Refs
        mainCodeRef: mainCodeRef,
        mainIDRef: mainIDRef,
        mainDataRef: mainDataRef,
        isPreparedRef: isPreparedRef,
        bootCode: bootCode,
        isSavingRef: isSavingRef,
        mountedRef: mountedRef,
        lockRef: lockRef,
        quickReloadTimerRef: quickReloadTimerRef,

        // Config
        mainConfig: mainConfig,
        mainTable: mainTable,
        mainEntityName: mainEntityName,
        mainEntityDisplay: mainEntityDisplay,

        // Functions
        loadContent: loadContent,
        load: load,
        reload: reload,
        setError: setError,
        getValidData: getValidData,
        saveOnFieldDataChanged: saveOnFieldDataChanged,
        onFieldDataChanged: onFieldDataChanged,
        navigateHome: navigateHome,
        navigateToReload: navigateToReload,

        // Computed
        isRenderPage: isRenderPage,
        isVisiblePage: isVisiblePage,
        isVisibleHeader: isVisibleHeader,
        isVisibleFooter: isVisibleFooter,
        isInValidData: isInValidData,
        sessionValid: sessionValid,
        isPermDeniedData: isPermDeniedData,
        renderLoading: renderLoading,
        renderAccessDenied: renderAccessDenied,
        renderNotExistData: renderNotExistData,
        sidebarContext: sidebarContext,

        // Router
        params: params,
        locationPath: locationPath,
        navigate: navigate,
        session: session
    };
}
